import { Link } from "react-router-dom";
import { ShieldCheck, Blueprint, Handshake, ArrowUpRight } from "@phosphor-icons/react";
import { Reveal } from "@/components/Reveal";
import { BOARDROOM_BG } from "@/constants/site";

const CARDS = [
  {
    number: "01",
    icon: ShieldCheck,
    title: "Scarce by Design",
    body: "Decades of Field CTO and technical GTM work, now practiced as high-leverage counsel. Limited concurrent engagements — selective fit is the model, not the marketing.",
    span: "lg:col-span-7",
    testId: "value-card-derisk",
  },
  {
    number: "02",
    icon: Blueprint,
    title: "Architecture & Judgment, Not Hours",
    body: "Deliverables are decisions, architectures, and sprint assets — never days in the field. Your team executes the motion; I design it and intervene where it matters.",
    span: "lg:col-span-5",
    testId: "value-card-velocity",
  },
  {
    number: "03",
    icon: Handshake,
    title: "The Rooms That Matter",
    body: "Must-win Global 2000 validation, security reviews, board alignment — I sit the rooms that close, then transfer the play to your bench.",
    span: "lg:col-span-12",
    testId: "value-card-retainer",
    wide: true,
  },
];

const TEASERS = [
  {
    metric: "11% → 50%+ live coverage expansion through phased, validated onboarding",
    slug: "replication-risk-scoring-preflight",
  },
  {
    metric: "75% faster AI deployment via governed data products",
    slug: "enterprise-data-product-factory-marketplace",
  },
  {
    metric: "$2.3M annual inventory carrying-cost reduction",
    slug: "telematics-supply-chain-telemetry-engine",
  },
];

export default function ValueProp() {
  return (
    <section
      id="why-us"
      data-testid="value-prop-section"
      className="px-6 lg:px-12 py-24 lg:py-32"
    >
      <div className="max-w-[1440px] mx-auto">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-6">
            01 — Why Me
          </p>
          <h2 className="font-serif tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white max-w-4xl leading-[1.1]">
            A Career in the Field.{" "}
            <span className="italic text-[#E6E4D9]">Now Practiced as Counsel.</span>
          </h2>
        </Reveal>

        <div className="mt-14 lg:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-px bg-white/10 border border-white/10">
          {CARDS.map((card, i) => (
            <Reveal
              key={card.number}
              delay={i * 0.12}
              className={`${card.span} bg-[#0A0A0A]`}
            >
              <article
                data-testid={card.testId}
                className="group relative h-full overflow-hidden p-10 lg:p-14 hover:bg-[#101010] transition-colors duration-500"
              >
                {card.wide && (
                  <>
                    <img
                      src={BOARDROOM_BG}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover opacity-15 transition-opacity duration-700 group-hover:opacity-25"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
                  </>
                )}
                <span className="absolute top-6 right-8 font-serif text-7xl lg:text-9xl text-white/5 select-none leading-none">
                  {card.number}
                </span>
                <div className="relative">
                  <card.icon
                    size={32}
                    weight="light"
                    className="text-[#E6E4D9] mb-8"
                  />
                  <h3 className="font-serif text-2xl sm:text-3xl text-white tracking-tight mb-5 max-w-md">
                    {card.title}
                  </h3>
                  <p className="text-base sm:text-lg font-light leading-relaxed text-white/70 max-w-lg">
                    {card.body}
                  </p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div data-testid="why-me-teasers" className="mt-10 flex flex-col gap-4">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
              Selected outcomes from anonymized engagements
            </span>
            <div className="flex flex-col sm:flex-row flex-wrap gap-x-12 gap-y-4">
              {TEASERS.map((t) => (
                <Link
                  key={t.slug}
                  to={`/insights/${t.slug}`}
                  data-testid={`why-me-teaser-${t.slug}`}
                  aria-label={`Outcome: ${t.metric}`}
                  className="group inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.12em] text-[#E6E4D9] hover:text-white transition-colors duration-300"
                >
                  {t.metric}
                  <ArrowUpRight
                    size={12}
                    weight="bold"
                    className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </Link>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
